import { useEffect, useState } from "react";
import { useSession } from "../lib/session";
import { apiLinkedSuppliersByOutlet, type Supplier } from "../lib/api";
import { Render } from "../render/Render";

/**
 * Suppliers — uses the CORRECTED endpoint shape observed on the original:
 *   GET /services/inventory/deliveryPreference/linkedSuppliersByOutlet
 *       ?outletId=<comma-separated outletIds>
 *
 * Note: my prior version called /linkedSuppliers?outletId=ONE and got
 * an empty `data:[]` back silently. The real endpoint name is
 * linkedSuppliersByOutlet and accepts a list.
 */
export function Suppliers() {
  const { session } = useSession();
  const [data, setData] = useState<Supplier[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const ids = session.outlets?.map((o) => o.outletId) ?? [];
    if (ids.length === 0) return;
    let cancelled = false;
    setData(null);
    setErr(null);
    apiLinkedSuppliersByOutlet(ids)
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r) ? r : ((r as { data?: Supplier[] })?.data ?? []);
        setData(list);
      })
      .catch((e) => !cancelled && setErr(String(e.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [session.outlets]);

  type Elem = { type: string; props: Record<string, unknown>; children: string[] };
  const elements: Record<string, Elem> = {
    root: {
      type: "Page",
      props: {
        title: "Suppliers",
        subtitle: session.outlets?.length
          ? `Linked across ${session.outlets.length} outlet${session.outlets.length === 1 ? "" : "s"}`
          : "No outlets linked.",
      },
      children: ["body"],
    },
    body: { type: "Section", props: { title: null, density: "regular" }, children: ["content"] },
  };

  if (!session.outlets?.length) {
    elements.content = { type: "EmptyState", props: { title: "No outlets", body: "Talk to your group admin." }, children: [] };
  } else if (err) {
    elements.content = { type: "Card", props: { title: "Could not load suppliers", tone: "danger", eyebrow: null }, children: ["err"] };
    elements.err = { type: "Markdown", props: { body: err }, children: [] };
  } else if (data === null) {
    elements.content = { type: "Skeleton", props: { lines: 6 }, children: [] };
  } else if (data.length === 0) {
    elements.content = {
      type: "EmptyState",
      props: {
        title: "No suppliers linked",
        body: "The test account has no delivery preferences set up. On a real account this is the supplier list keyed by deliveryPreference.linkedSuppliersByOutlet.",
      },
      children: [],
    };
  } else {
    elements.body.children = data.map((_, i) => `s-${i}`);
    data.forEach((s, i) => {
      elements[`s-${i}`] = {
        type: "SupplierRow",
        props: {
          supplierId: s.supplierId ?? `?${i}`,
          supplierName: s.supplierName ?? "(unnamed supplier)",
          deliveryDays: s.deliveryDays ?? null,
          minOrder: s.minOrder ?? null,
        },
        children: [],
      };
    });
  }

  return <Render spec={{ root: "root", elements } as never} />;
}
